/**
 * Connected-app access ledger (DRE-322): display helpers over the
 * `api/connected-app-activity.ts` types from @dreamai/types.
 */

import type { ConnectedAppAccessEvent } from '@dreamai/types';

export type {
  ConnectedAppAccessEvent,
  ConnectedAppProviderUsage,
  GetConnectedAppActivityResponse,
  GetConnectedAppActivitySummaryResponse,
} from '@dreamai/types';

export interface ProviderName {
  /** Bold app name, e.g. "Google". */
  name: string;
  /** Optional qualifier shown in brackets after the name, e.g. "Email, Calendar". */
  detail?: string;
}

const PROVIDER_NAMES: Record<string, ProviderName> = {
  GOOGLE: { name: 'Google', detail: 'Email, Calendar' },
  GOOGLE_WORKSPACE: { name: 'Google', detail: 'Docs, Sheets, Drive' },
  MICROSOFT: { name: 'Outlook' },
  EMAIL: { name: 'Email' },
  LINEAR: { name: 'Linear' },
  NOTION: { name: 'Notion' },
  MONDAY: { name: 'Monday.com' },
  SLACK_USER: { name: 'Slack' },
  HUBSPOT: { name: 'HubSpot' },
  ATTIO: { name: 'Attio' },
  ASANA: { name: 'Asana' },
  TODOIST: { name: 'Todoist' },
  XERO: { name: 'Xero' },
  ZOOM: { name: 'Zoom' },
  GRANOLA: { name: 'Granola' },
  LUSHA: { name: 'Lusha' },
  DEVIN: { name: 'Devin' },
  COMPOSIO: { name: 'Composio' },
};

export const providerName = (provider: string): ProviderName =>
  PROVIDER_NAMES[provider] ?? {
    name: provider
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' '),
  };

/** Single-string form, e.g. "Google (Email, Calendar)", for selects and labels. */
export const providerDisplayName = (provider: string): string => {
  const { name, detail } = providerName(provider);
  return detail ? `${name} (${detail})` : name;
};

const CHANNEL_LABELS: Record<string, string> = {
  web_app: 'web chat',
  webchat: 'web chat',
  whatsapp: 'WhatsApp',
  phone_call: 'a phone call',
  email: 'email',
  sms: 'SMS',
  slack: 'Slack',
};

export const channelLabel = (channel: string | null): string | null =>
  channel ? (CHANNEL_LABELS[channel] ?? channel.replace(/_/g, ' ')) : null;

/** Consecutive accesses to one Connected App (same account) within this gap collapse into one row. */
export const BATCH_GAP_MS = 60 * 60 * 1000;

export interface EventBatch {
  /** Stable key: id of the newest event in the batch. */
  id: string;
  provider: string;
  accountLabel: string | null;
  isDisconnected: boolean;
  /** Newest first, as returned by the API. */
  events: ConnectedAppAccessEvent[];
  /** ISO timestamps of the oldest and newest access in the batch. */
  from: string;
  to: string;
}

/**
 * Collapse runs of accesses to the same Connected App (and account) into
 * batches. Events must be newest-first; a run continues while each event is
 * within `gapMs` of the one before it. Single events become batches of one.
 */
export const batchEvents = (
  events: ConnectedAppAccessEvent[],
  gapMs: number = BATCH_GAP_MS,
): EventBatch[] => {
  const batches: EventBatch[] = [];
  for (const event of events) {
    const last = batches[batches.length - 1];
    const oldest = last?.events[last.events.length - 1];
    if (
      last &&
      oldest &&
      last.provider === event.provider &&
      last.accountLabel === event.accountLabel &&
      Date.parse(oldest.createdAt) - Date.parse(event.createdAt) <= gapMs
    ) {
      last.events.push(event);
      last.from = event.createdAt;
    } else {
      batches.push({
        id: event.id,
        provider: event.provider,
        accountLabel: event.accountLabel,
        isDisconnected: event.isDisconnected,
        events: [event],
        from: event.createdAt,
        to: event.createdAt,
      });
    }
  }
  return batches;
};

/** Distinct values in first-seen order with their counts. */
export const countDistinct = (
  values: string[],
): { value: string; count: number }[] => {
  const counts = new Map<string, number>();
  values.forEach((v) => counts.set(v, (counts.get(v) ?? 0) + 1));
  return Array.from(counts, ([value, count]) => ({ value, count }));
};

export interface EventContext {
  /** What the user (or routine) was trying to get done, if known. */
  task: string | null;
  /** Where the request came from: "Asked on WhatsApp", "Routine", … */
  source: string;
}

/** Plain-English "why" for an event, split into the task and its origin. */
export const describeContext = (
  event: ConnectedAppAccessEvent,
): EventContext => {
  const via = channelLabel(event.channel);
  switch (event.trigger) {
    case 'routine':
      return {
        task: event.taskDescription,
        source: event.routineName ? `Routine: ${event.routineName}` : 'Routine',
      };
    case 'background_sync':
      return {
        task: 'Keeping your inbox up to date',
        source: 'In the background',
      };
    case 'system':
      return { task: event.taskDescription, source: 'System' };
    default:
      return {
        task: event.taskDescription,
        source: via ? `Asked on ${via}` : 'Asked by you',
      };
  }
};
